<template>
  <b-modal
    id="castCreateModal"
    ref="castCreateModal"
    title="방송 만들기"
    @show="onShow"
    centered>
    <b-form-group label="방송 장치 선택:">
      <b-form-select
        v-model="constraints.video.deviceId"
        :options="videoDevices"
        @change="onDeviceChanged('video')"
        class="mb-1">
        <option :value="null" slot="first">
          🎥 카메라 목록..
        </option>
      </b-form-select>
      <p class="very-small mb-1">{{ videoDeviceInfo }}</p>
      <b-form-select
        v-model="constraints.audio.deviceId"
        :options="audioDevices"
        @change="onDeviceChanged('audio')"
        class="mb-1">
        <option :value="null" slot="first">
          🎤 마이크 목록..
        </option>
      </b-form-select>
      <p class="very-small">{{ audioDeviceInfo }}</p>
    </b-form-group>
    <b-form-group label="미디어 설정:" inline>
      <b-form-select
        v-model="selectedResolution"
        :options="supportedResolutions"
        @change="onResolutionChanged()"
        class="mb-1"
        size="sm"
        placeholder="resolution">
        <option :value="null" slot="first">해상도를 선택하세요</option>
      </b-form-select>

      <b-input-group class="mb-1" size="sm" prepend="화면 넓이" append="px">
        <b-form-input
          v-model.trim="constraints.video.width.exact"
          type="number"
          :state="widthState">
        </b-form-input>
      </b-input-group>

      <b-input-group class="mb-3" size="sm" prepend="화면 높이" append="px">
        <b-form-input
          v-model.trim="constraints.video.height.exact"
          type="number"
          :state="heightState">
        </b-form-input>
      </b-input-group>

      <b-input-group class="mb-1" size="sm" prepend="대역폭" append="bps">
        <b-form-input
          v-model.trim="maxBandwidth"
          type="number">
        </b-form-input>
      </b-input-group>

      <b-input-group class="mb-1" size="sm" prepend="프레임레이트" append="fps">
        <b-form-input
          v-model.trim="constraints.video.frameRate.exact"
          type="number"
          :state="frameRateState">
        </b-form-input>
      </b-input-group>
      <b-row>
        <b-col>
          <b-form-select
            size="sm"
            v-model="codec"
            :options="codecsTemplate">
            <option :value="null" slot="first">코덱을 설정하세요</option>
          </b-form-select>
        </b-col>
        <b-col cols="3" class="align-self-center">
          <b-form-checkbox
            id="simulcast"
            v-model="simulcast">
            Simulcast
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-form-group>

    <b-form-group label="방 정보:">
      <b-input-group class="mb-1" prepend="제목">
        <b-form-input
          placeholder="🎬 방 제목을 입력하세요"
          v-model.trim="name"
          type="text">
        </b-form-input>
      </b-input-group>

       <b-input-group class="mb-1" prepend="상금" :append="numToKor(prize)">
         <b-form-input
           placeholder="💰 상금을 입력하세요"
           v-model.trim.number="prize"
           type="number">
         </b-form-input>
       </b-input-group>

       <b-input-group class="mb-1" prepend="변환비(옵션)" :append="numToKor(prizeRatio)">
         <b-form-input
           placeholder="💰 상금 변환비를 입력하세요"
           v-model.trim.number="prizeRatio"
           type="number">
         </b-form-input>
       </b-input-group>

       <b-input-group prepend="풀이시간" append="초">
         <b-form-input
           placeholder="풀이시간을 초로 입력하세요"
           v-model.trim.number="quizTimeLimit"
           type="number">
         </b-form-input>
       </b-input-group>
    </b-form-group>

    <div slot="modal-footer" class="container px-0">
      <div class="row mx-0">
        <b-alert :show="alertMessage" variant="danger" class="w-100">{{alertMessage}}</b-alert>
      </div>
      <div class="row w-100 d-flex mx-0">
        <button class="mr-auto btn bg-danger progress-bar progress-bar-striped progress-bar-animated d-inline-block" @click="createRoom('PROD')">
          프로덕션 방송 시작
        </button>
        <button class="mr-2 btn btn-secondary" @click="createRoom('TEST')">
          테스트 방송 시작
        </button>
        <button class="btn btn-secondary" @click="hideModal('castCreateModal')">
          취소
        </button>
      </div>
    </div>

    <v-dialog/>
  </b-modal>
</template>

<script>
import {
  mapGetters,
  mapState
} from 'vuex'
import numToKor from '@/mixins/numToKor'
import alert from '@/mixins/alert'
import l from '@/vendor/logger'
import Constraints from '@/models/constraints'
import format from 'date-fns/format'
import ko from 'date-fns/locale/ko'
import * as _ from 'ramda'

export default {
  name: 'CastCreateModal',

  mixins: [ numToKor, alert ],

  data () {
    return {
      alertMessage: null,
      name: null,
      prize: 10,
      prizeRatio: 150,
      quizTimeLimit: 10,
      videoDevices: [],
      audioDevices: [],
      videoDeviceInfo: '',
      audioDeviceInfo: '',
      constraints: { ...Constraints() },
      maxBandwidth: '',
      codecsTemplate: ['H264', 'VP8', 'VP9'],
      codec: 'VP8',
      supportedResolutions: [],
      selectedResolution: null,
      simulcast: false,
      resolutionsTemplate: [
        { label: '8K', value: '7680 x 4320', width: 7680, height: 4320, ratio: '16:9' },
        { label: '4K', value: '4096 x 2160', width: 4096, height: 2160, ratio: '16:9' },
        { label: '1080(FHD)', value: '1920 x 1080', width: 1920, height: 1080, ratio: '16:9' },
        { label: '1080(FHD) portrait', value: '1080 x 1920', width: 1080, height: 1920, ratio: '16:9' },
        { label: 'UXGA', value: '1600 x 1200', width: 1600, height: 1200, ratio: '4:3' },
        { label: '720(HD)', value: '1280 x 720', width: 1280, height: 720, ratio: '16:9' },
        { label: '720(HD) portrait', value: '720 x 1280', width: 720, height: 1280, ratio: '16:9' },
        { label: 'VGA', value: '640 x 480', width: 640, height: 480, ratio: '4:3' },
        { label: 'CIF', value: '352 x 288', width: 352, height: 288, ratio: '4:3' },
        { label: 'QVGA', value: '320 x 240', width: 320, height: 240, ratio: '4:3' }
      ]
    }
  },

  computed: {
    ...mapGetters('user', [
      'isSignIn',
      'user'
    ]),
    ...mapState([
      'rid'
    ]),
    ...mapGetters('rooms', [
      'prodRooms',
      'testRooms'
    ]),
    ...mapState('media', {
      lastSuccessConstraints: 'constraints',
      lastSuccessMaxBandwidth: 'maxBandwidth',
      lastSuccessCodec: 'codec'
    }),

    widthState () {
      return this.videoDeviceInfo.width &&
        ((this.constraints.video.width.exact <= this.videoDeviceInfo.width.max &&
        this.constraints.video.width.exact >= this.videoDeviceInfo.width.min) ||
          !this.constraints.video.width.exact ? null : false)
    },

    heightState () {
      return this.videoDeviceInfo.height &&
        ((this.constraints.video.height.exact <= this.videoDeviceInfo.height.max &&
        this.constraints.video.height.exact >= this.videoDeviceInfo.height.min) ||
          !this.constraints.video.height.exact ? null : false)
    },

    frameRateState () {
      return this.videoDeviceInfo.frameRate &&
        ((this.constraints.video.frameRate.exact <= this.videoDeviceInfo.frameRate.max &&
        this.constraints.video.frameRate.exact >= this.videoDeviceInfo.frameRate.min) ||
          !this.constraints.video.frameRate.exact ? null : false)
    }
  },

  methods: {
    hideModal (ref) {
      this.alertMessage = null
      this.$refs[ref].hide()
    },

    onShow (evt) {
      this.getDevices()
      this.loadLastSuccessSettings()
    },

    loadLastSuccessSettings () {
      this.$nextTick(() => {
        this.$store.dispatch('media/loadSettings')
        !!this.lastSuccessConstraints && !!this.lastSuccessMaxBandwidth && !!this.lastSuccessCodec
          ? (() => {
            l.d('CastCreateModal/loadLastSuccessSettings', this.$store.getters)
            this.constraints = JSON.parse(JSON.stringify(this.lastSuccessConstraints))
            this.maxBandwidth = JSON.parse(JSON.stringify(this.lastSuccessMaxBandwidth))
            this.codec = JSON.parse(JSON.stringify(this.lastSuccessCodec))
            this.onDeviceChanged('video')
            this.onDeviceChanged('audio')
          })()
          : this.$store.commit('media/clearMedia')
      })
    },

    async getDevices () {
      try {
        // NOTE: 퍼미션 없으면 기기의 ID만 받아옴. 라벨명을 받으려면 퍼미션 필요
        // Promise.all[
        //  navigator.permission.query({ name: 'camera'}),
        //  navigator.permission.query({ name: 'microphone'})
        // ]

        // NOTE: 일단 퍼미션은 getUserMedia로 얻는것으로
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        const userDevices = await navigator.mediaDevices.enumerateDevices()
        const optionDevices = userDevices.map((x) => {
          l.d('castCreateModal/getDevices', x)
          return {
            text: x.label,
            value: x.deviceId,
            kind: x.kind
          }
        })
        this.audioDevices = optionDevices.filter(x => x.kind === 'audioinput')
        this.videoDevices = optionDevices.filter(x => x.kind === 'videoinput')
        mediaStream.getTracks().forEach(track => track.stop())
      } catch (err) {
        l.e('castCreateModal/getDevices', err, err.name, err.message)
        this.showAlert({
          error: err
        })
      }
    },

    onDeviceChanged (type) {
      l.d('CastCreateModal/onDeviceChanged', type)
      this.$nextTick(() => {
        this.constraints[type].deviceId
          ? this.getDeviceCapabilities(type, this.constraints[type].deviceId)
          : (() => {
            this[`${type}DeviceInfo`] = ''
            type === 'video' && (this.supportedResolutions = [])
          })()
      })
    },

    onResolutionChanged () {
      l.d('CastCreateModal/onResolutionChanged')
      this.$nextTick(() => {
        const resolution = this.supportedResolutions.find(x => x.value === this.selectedResolution)
        this.constraints.video.height.exact = resolution.attributes.height
        this.constraints.video.width.exact = resolution.attributes.width
      })
    },

    async getDeviceCapabilities (type, deviceId) {
      const videoConstraints = {
        audio: false,
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined
        }
      }

      const audioConstraints = {
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined
        },
        video: false
      }

      let constraints = !type ? undefined
        : type === 'audio' ? audioConstraints
          : type === 'video' ? videoConstraints
            : new Error('getDeviceCapabilities/unexpected_args', type)

      try {
        l.d('CastCreateModal/getDeviceCapabilities/constraints', constraints)
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
        mediaStream.getTracks().forEach((track) => {
          this[`${type}DeviceInfo`] = _.omit(['deviceId', 'groupId'], mediaStream.getTrackById(track.id).getCapabilities())
          track.stop()
        })
        type === 'video' && this.getSupportedResolution()
      } catch (err) {
        l.d('CastCreateModal/getDeviceCapabilities', err)
      }
    },

    getSupportedResolution () {
      this.supportedResolutions = this.resolutionsTemplate.filter((x) => {
        return x.width <= this.videoDeviceInfo.width.max &&
          x.width >= this.videoDeviceInfo.width.min &&
          x.height <= this.videoDeviceInfo.height.max &&
          x.height >= this.videoDeviceInfo.height.min
      }).map((x) => {
        return {
          text: `${x.label}     ${x.value}      ${x.ratio}`,
          value: x.value,
          attributes: {
            width: x.width,
            height: x.height
          }
        }
      })
    },

    createRoom (mode) {
      l.d('live/createRoom', mode, this.prodRooms)
      if (mode === 'TEST' && this.testRooms) {
        l.d('live/createRoom/too many test rooms')
        this.alertMessage = '한번에 하나의 TEST 방송만 생성가능합니다.'
        return
      } else if (mode === 'PROD' && this.prodRooms) {
        l.d('live/createRoom/too many prod rooms')
        this.alertMessage = '한번에 하나의 PRODUCTION 방송만 생성가능합니다.'
        return
      }
      const newRid = Math.random().toString(36).substring(2, 15)
      const dayOfWeek = format(Date.now(), 'dddd', { locale: ko })

      if (!this.name) {
        this.name = `${this.user.displayName}님의 ${dayOfWeek} 방송`
      }

      this.$store.dispatch('setMode', mode)
      this.$store.commit('setRid', newRid)
      this.$store.commit('room/setRoom', {
        id: newRid,
        name: this.name,
        prize: this.prize,
        prizeRatio: this.prizeRatio,
        quizTimeLimit: this.quizTimeLimit,
        creator: this.user.uid,
        creatorName: this.user.displayName,
        photoURL: this.user.photoURL,
        fps: this.constraints.video.frameRate.exact
      })
      this.$store.commit('media/setConstraints', this.constraints)
      this.$store.commit('media/setMaxBandwidth', this.maxBandwidth)
      this.$store.commit('media/setCodec', this.codec)
      this.$store.commit('media/setSimulcast', this.simulcast)

      this.hideModal('castCreateModal')
      this.$router.push({ name: 'cast' })
    }
  }
}
</script>

<style scoped>
.very-small {
  font-size: 0.3em
}
</style>
